import React, {useMemo, useRef, useState} from "react";
import * as XLSX from "xlsx";
import {AlertTriangle, CheckCircle2, Download, FileSpreadsheet, FileUp, RotateCcw, UploadCloud} from "lucide-react";
import {cn} from "../../utils/cn";
import SlideOver from "../ui/SlideOver";
import Button from "../ui/Button";
import Pill from "../ui/Pill";
import Spinner from "../ui/Spinner";

/**
 * Generic bulk-import slide-over: template download (CSV / Excel),
 * drag-and-drop upload, client-side validation with per-row error
 * reporting, then a serial commit driven by the host page.
 *
 * Props:
 *   entity      "assets" | "users" — logging prefix + file names
 *   title / subtitle
 *   columns     [{key, label, required, hint, validate(value, row) -> string|null, normalize(value) -> any,
 *                 requiredIf(row) -> bool — conditional requirement; only sees columns
 *                 declared BEFORE this one (cells are parsed in column order)}]
 *   sampleRows  [{key: value}] — rows written into the downloadable template
 *   commitRow   async (row) => void — throws on per-row API failure
 *   onDone      ({ok, failed}) => void — called after a commit finishes
 *
 * Phases: idle → validating → review → committing → done.
 *
 * Logging (enterprise audit requirements):
 *   console.info  — template downloads, upload initiation, parse + commit summaries
 *   console.debug — header mapping, payload previews, per-row validation/commit failures
 */
export default function BulkUploadSlideOver({
  entity,
  title,
  subtitle,
  columns,
  sampleRows = [],
  commitRow,
  open,
  onClose,
  onDone,
}) {
  const [phase, setPhase] = useState("idle");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState({valid: [], errors: [], total: 0});
  const [progress, setProgress] = useState({done: 0, total: 0});
  const [commitResult, setCommitResult] = useState({ok: 0, failed: []});
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const tag = `[BulkUpload:${entity}]`;

  // Normalised header text -> column key ("Asset Name", "asset_name", "assetName" all match)
  const headerMap = useMemo(() => {
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const map = {};
    columns.forEach((c) => {
      map[norm(c.key)] = c.key;
      map[norm(c.label)] = c.key;
    });
    return {norm, map};
  }, [columns]);

  const reset = () => {
    setPhase("idle");
    setFileName("");
    setParsed({valid: [], errors: [], total: 0});
    setProgress({done: 0, total: 0});
    setCommitResult({ok: 0, failed: []});
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    reset();
    onClose && onClose();
  };

  // ── Template downloads ─────────────────────────────────────────
  const downloadTemplate = (format) => {
    console.info(`${tag} Template download triggered`, {format, columns: columns.map((c) => c.label)});
    const headers = columns.map((c) => c.label);
    const rows = sampleRows.map((r) => columns.map((c) => r[c.key] ?? ""));
    if (format === "csv") {
      const esc = (v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
      const blob = new Blob([`﻿${csv}`], {type: "text/csv;charset=utf-8"});
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${entity}_bulk_template.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } else {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws["!cols"] = columns.map((c) => ({wch: Math.max(c.label.length + 4, 16)}));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      XLSX.writeFile(wb, `${entity}_bulk_template.xlsx`);
    }
  };

  // ── Parse + validate ───────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    console.info(`${tag} File upload initiated`, {name: file.name, size: file.size, type: file.type});
    setFileName(file.name);
    setPhase("validating");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, {type: "array"}); // handles .xlsx, .xls AND .csv
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(sheet, {header: 1, defval: "", raw: false});
      if (!matrix.length) throw new Error("The file is empty.");

      // Map file headers to known columns
      const fileHeaders = matrix[0].map((h) => String(h).trim());
      const colIndex = {}; // column key -> index in the file
      fileHeaders.forEach((h, i) => {
        const key = headerMap.map[headerMap.norm(h)];
        if (key && colIndex[key] === undefined) colIndex[key] = i;
      });
      console.debug(`${tag} Header mapping resolved`, {fileHeaders, colIndex});

      const missingHeaders = columns.filter((c) => c.required && colIndex[c.key] === undefined);
      if (missingHeaders.length) {
        throw new Error(`Missing required column(s): ${missingHeaders.map((c) => c.label).join(", ")}. Download the template to see the expected format.`);
      }

      const dataRows = matrix.slice(1).filter((r) => r.some((v) => String(v).trim() !== ""));
      const valid = [];
      const errors = [];
      const seen = {}; // duplicate detection per unique-marked column

      dataRows.forEach((raw, i) => {
        const lineNo = i + 2; // 1-based + header row
        const row = {};
        const rowErrors = [];
        columns.forEach((c) => {
          let v = colIndex[c.key] !== undefined ? String(raw[colIndex[c.key]] ?? "").trim() : "";
          if (c.normalize) v = c.normalize(v);
          row[c.key] = v;
          if (!String(v).length && (c.required || (c.requiredIf && c.requiredIf(row)))) {
            rowErrors.push(`Missing required field '${c.label}'`);
            return;
          }
          if (String(v).length && c.validate) {
            const err = c.validate(v, row);
            if (err) rowErrors.push(`${c.label}: ${err}`);
          }
          if (String(v).length && c.unique) {
            const dupKey = `${c.key}:${String(v).toLowerCase()}`;
            if (seen[dupKey]) rowErrors.push(`Duplicate ${c.label} '${v}' (first used on row ${seen[dupKey]})`);
            else seen[dupKey] = lineNo;
          }
        });
        if (rowErrors.length) {
          rowErrors.forEach((msg) => console.debug(`${tag} Row ${lineNo} validation failed: ${msg}`));
          errors.push({line: lineNo, messages: rowErrors});
        } else {
          valid.push({line: lineNo, data: row});
        }
      });

      console.info(`${tag} Successfully parsed ${valid.length} ${entity} out of ${dataRows.length}`, {invalidRows: errors.length});
      console.debug(`${tag} Valid payload preview (first 3)`, valid.slice(0, 3).map((v) => v.data));
      setParsed({valid, errors, total: dataRows.length});
      setPhase("review");
    } catch (e) {
      console.debug(`${tag} Parse failure`, {message: e.message});
      setParsed({valid: [], errors: [{line: "—", messages: [e.message || "Could not read the file."]}], total: 0});
      setPhase("review");
    }
  };

  // ── Commit ─────────────────────────────────────────────────────
  const commit = async () => {
    const rows = parsed.valid;
    console.info(`${tag} Commit started`, {rows: rows.length, file: fileName});
    setPhase("committing");
    setProgress({done: 0, total: rows.length});
    let ok = 0;
    const failed = [];
    for (const {line, data} of rows) {
      try {
        await commitRow(data);
        ok++;
      } catch (e) {
        const msg = e?.response?.data?.responseData || e?.response?.data?.message || e.message || "Request failed";
        console.debug(`${tag} Row ${line} commit failed: ${typeof msg === "string" ? msg : "Request failed"}`);
        failed.push({line, message: typeof msg === "string" ? msg : "Request failed"});
      }
      setProgress((p) => ({...p, done: p.done + 1}));
    }
    console.info(`${tag} Commit finished — ${ok} created, ${failed.length} failed out of ${rows.length}`);
    setCommitResult({ok, failed});
    setPhase("done");
    onDone && onDone({ok, failed});
  };

  // ── Drag & drop plumbing ───────────────────────────────────────
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const requiredLabels = columns.filter((c) => c.required).map((c) => c.label);

  return (
    <SlideOver open={open} onOpenChange={(o) => !o && close()} title={title} subtitle={subtitle} size="lg">
      <SlideOver.Body>
        <div className="space-y-4">

          {/* Step 1 — template */}
          <section className="rounded-lg border border-ink-200 p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink-900">1. Download the template</p>
                <p className="text-xs text-ink-500 mt-0.5">
                  Required columns: {requiredLabels.join(", ")}.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="secondary" size="sm" icon={Download} onClick={() => downloadTemplate("csv")}>CSV</Button>
                <Button variant="secondary" size="sm" icon={FileSpreadsheet} onClick={() => downloadTemplate("xlsx")}>Excel</Button>
              </div>
            </div>
          </section>

          {/* Step 2 — drop zone */}
          {(phase === "idle" || phase === "validating") && (
            <section
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => phase === "idle" && inputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Upload a filled template"
              className={cn(
                "rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors",
                dragActive ? "border-brand-500 bg-brand-50/50" : "border-ink-300 hover:border-brand-400 hover:bg-ink-50"
              )}
            >
              {phase === "validating" ? (
                <div className="flex flex-col items-center gap-2 text-ink-600">
                  <Spinner size="md" />
                  <p className="text-sm font-medium">Validating {fileName}…</p>
                  <p className="text-xs text-ink-500">Checking formatting and required fields</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-ink-600">
                  <UploadCloud className="h-8 w-8 text-ink-400" aria-hidden="true" />
                  <p className="text-sm font-medium">2. Drag &amp; drop the filled template here</p>
                  <p className="text-xs text-ink-500">or click to browse — .csv, .xlsx, .xls</p>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </section>
          )}

          {/* Step 3 — review */}
          {phase === "review" && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-ink-700 inline-flex items-center gap-1.5 min-w-0">
                  <FileUp className="h-4 w-4 text-ink-400 shrink-0" aria-hidden="true" />
                  <span className="truncate font-medium">{fileName}</span>
                </span>
                <Pill tone={parsed.valid.length ? "success" : "neutral"} size="sm">{parsed.valid.length} valid</Pill>
                {parsed.errors.length > 0 && <Pill tone="danger" size="sm">{parsed.errors.length} with errors</Pill>}
                <button type="button" onClick={reset} className="text-xs font-medium text-brand-700 hover:underline inline-flex items-center gap-1 ml-auto">
                  <RotateCcw className="h-3 w-3" aria-hidden="true" /> Choose another file
                </button>
              </div>

              {parsed.errors.length > 0 && (
                <div className="rounded-lg border border-danger-200 bg-danger-50/50 p-3 max-h-48 overflow-y-auto">
                  <p className="text-xs font-semibold text-danger-700 uppercase tracking-wider mb-2 inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Fix these rows in your file
                  </p>
                  <ul className="space-y-1">
                    {parsed.errors.slice(0, 50).map((e, i) => (
                      <li key={i} className="text-xs text-danger-800">
                        <span className="font-mono font-semibold">Row {e.line}:</span> {e.messages.join("; ")}
                      </li>
                    ))}
                    {parsed.errors.length > 50 && (
                      <li className="text-xs text-danger-700 italic">…and {parsed.errors.length - 50} more — see console for the full map.</li>
                    )}
                  </ul>
                </div>
              )}

              {parsed.valid.length > 0 && (
                <div className="rounded-lg border border-ink-200 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-ink-50 text-ink-500 uppercase tracking-wider">
                        <th className="text-left font-semibold px-2.5 py-1.5">Row</th>
                        {columns.map((c) => (
                          <th key={c.key} className="text-left font-semibold px-2.5 py-1.5 whitespace-nowrap">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.valid.slice(0, 5).map(({line, data}) => (
                        <tr key={line} className="border-t border-ink-100">
                          <td className="px-2.5 py-1.5 font-mono text-ink-400">{line}</td>
                          {columns.map((c) => (
                            <td key={c.key} className="px-2.5 py-1.5 text-ink-800 max-w-[180px] truncate" title={String(data[c.key] ?? "")}>
                              {String(data[c.key] ?? "") || <span className="text-ink-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.valid.length > 5 && (
                    <p className="text-2xs text-ink-500 px-2.5 py-1.5 border-t border-ink-100">…and {parsed.valid.length - 5} more valid rows</p>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Committing */}
          {phase === "committing" && (
            <section className="rounded-lg border border-ink-200 p-6 text-center space-y-3">
              <Spinner size="md" className="mx-auto" />
              <p className="text-sm font-medium text-ink-800">Uploading {progress.done} / {progress.total}…</p>
              <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden max-w-xs mx-auto">
                <div
                  className="h-full bg-brand-600 transition-all duration-150"
                  style={{width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`}}
                />
              </div>
            </section>
          )}

          {/* Done */}
          {phase === "done" && (
            <section className="space-y-3">
              <div className={cn(
                "rounded-lg border p-4 flex items-start gap-3",
                commitResult.failed.length ? "border-warning-300 bg-warning-50/60" : "border-success-300 bg-success-50/60"
              )}>
                {commitResult.failed.length
                  ? <AlertTriangle className="h-5 w-5 text-warning-600 shrink-0 mt-0.5" aria-hidden="true" />
                  : <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0 mt-0.5" aria-hidden="true" />}
                <div>
                  <p className="text-sm font-semibold text-ink-900">
                    {commitResult.ok} of {commitResult.ok + commitResult.failed.length} rows created
                  </p>
                  {commitResult.failed.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {commitResult.failed.slice(0, 20).map((f, i) => (
                        <li key={i} className="text-xs text-ink-700">
                          <span className="font-mono font-semibold">Row {f.line}:</span> {f.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <button type="button" onClick={reset} className="text-xs font-medium text-brand-700 hover:underline inline-flex items-center gap-1">
                <RotateCcw className="h-3 w-3" aria-hidden="true" /> Upload another file
              </button>
            </section>
          )}
        </div>
      </SlideOver.Body>

      <SlideOver.Footer>
        <Button variant="secondary" onClick={close}>{phase === "done" ? "Close" : "Cancel"}</Button>
        {phase === "review" && (
          <Button
            variant="primary"
            icon={UploadCloud}
            disabled={parsed.valid.length === 0}
            onClick={commit}
          >
            Upload {parsed.valid.length} valid row{parsed.valid.length === 1 ? "" : "s"}
          </Button>
        )}
      </SlideOver.Footer>
    </SlideOver>
  );
}
